/* ExposureConfigEvidence.js */

"use strict";
/**
 * Config-determination evidence for the per-CVE drawer.
 *
 * LEADS WITH THE COMMAND, NOT THE PATTERN. The matcher's regex is the implementation; the vendor's
 * published command ("show running-config | include ip sla server twamp") is what an operator can
 * paste into a terminal to check our work. That reproducibility is the whole point of showing
 * evidence at all, so the command is the first thing on the row and the pattern sits behind the
 * disclosure.
 *
 * THE REFUSAL CASE IS THE ONE THAT MATTERS. Measured on the reference device, 5 of 8 real checks
 * came back "no match, and the advisory never says what absence means" — the check ran and could
 * not clear the device. If that renders as "we found nothing, you're fine", the trust model
 * inverts, so it is stated as an explicit refusal rather than left to the absence of a result.
 *
 * ORDERED BY WHAT WAS LEARNED, not by rule order. Rows that moved the verdict render first and in
 * full; rows that could not clear the device collapse to one summary line. Otherwise the single
 * informative row is visually identical to the four uninformative ones sitting on top of it.
 */

const OUTCOME_LABELS = {
    matched: "found",
    absent: "no match",
    invalid: "check did not run",
};

/** Rows whose outcome changed, or could have changed, the verdict. */
function isDecisive(row) {
    if (!row) {
        return false;
    }
    // A check the SCORER refused to act on decided nothing, whatever its shape suggests. Without
    // this, a matcher blocked by corpus mismatch or OS scope rendered first and in full, above the
    // rows that actually moved the verdict — the opposite of what ranking by informativeness means.
    if (row.clearanceBlockedReason) {
        return false;
    }
    if (row.outcome === "matched") {
        return true;
    }
    // An absent enabling line only decides anything when the advisory says absence clears.
    return row.outcome === "absent" && Boolean(row.sourceQuote);
}

/**
 * Split evidence into the rows worth reading and the ones worth counting.
 *
 * @param {Array} evidence configEvidence from the verdict; null when scored before this existed.
 * @returns {{legacy: boolean, decisive: Array, inconclusive: Array}}
 */
function partitionConfigEvidence(evidence) {
    if (evidence === null || evidence === undefined) {
        // Null and [] are different claims: "scored before config checks existed" versus "checks
        // ran and matched nothing". Same distinction tagEvidence draws.
        return {legacy: true, decisive: [], inconclusive: []};
    }
    const rows = Array.isArray(evidence) ? evidence : [];
    return {
        legacy: false,
        decisive: rows.filter(isDecisive),
        inconclusive: rows.filter(function (row) {
            return !isDecisive(row);
        }),
    };
}

/** "found · 2 of 12 lines" / "no match · 318 lines searched · captured Aug 11 18:34". */
function describeOutcome(row, linesSearched) {
    const label = OUTCOME_LABELS[row && row.outcome] || String((row && row.outcome) || "");
    const parts = [label];
    if (row && row.outcome === "matched") {
        const shown = (row.matchedLines || []).length;
        const total = typeof row.totalMatches === "number" ? row.totalMatches : shown;
        parts.push(
            total > shown
                ? shown + " of " + total + " lines"
                : total + " line" + (total === 1 ? "" : "s")
        );
    } else if (row && row.outcome === "absent" && linesSearched) {
        // The line count is what makes a green off a truncated config visible rather than silent.
        parts.push(linesSearched + " lines searched");
    }
    return parts.join(" · ");
}

/**
 * What is the BEST this check could do, stated before anyone acts on it.
 *
 * Measured on the reviewed device: 5 of 8 config checks could not clear it whatever they found,
 * because the advisory never says what an absence means. That is the majority outcome, not an edge
 * case, and it is knowable in advance from `absenceMeans`. A panel that only reveals it afterwards
 * teaches an operator that the checks are decorative — they run a command, wait, see the verdict
 * unchanged, and correctly conclude the affordance does nothing. Confirming a weak version-only red
 * into a config-confirmed red is real triage value, so the check is still worth offering; it just
 * must not over-promise.
 */
function describeCeiling(row) {
    if (!row) {
        return "";
    }
    // A corpus mismatch overrides everything below: whatever the advisory says absence means, this
    // check cannot act on it, because the pattern was searched against different text.
    if (row.clearanceBlockedReason) {
        return "cannot clear this CVE";
    }
    if (row.meaning === "disabled") {
        // A disable line in this device's own config is device-authoritative, so finding one clears.
        return "can clear this CVE if the disable line is present";
    }
    if (row.absenceMeans === "notAffected") {
        return "can clear this CVE";
    }
    if (row.absenceMeans === "unknown") {
        return "can only confirm — the advisory does not say what absence means";
    }
    return "";
}

/**
 * True when the check could not run because there is no configuration to search.
 *
 * Distinct from every other failure: the check is fine, the rule is fine, the DEVICE simply has no
 * captured config. 20 of 57 devices were in that state when this was written, and a blank or generic
 * row there reads as "we looked and found nothing" — the precise inversion of the truth, and the one
 * the ledger exists to prevent.
 */
function isMissingSnapshot(row) {
    return Boolean(
        row &&
            row.outcome === "invalid" &&
            /no usable configuration snapshot|no configuration snapshot/i.test(
                row.invalidReason || ""
            )
    );
}

/**
 * The meaning line for a clearance licensed by the advisory's own sentence.
 *
 * Named, because the panel renders that sentence VERBATIM on the same row. Restating it in our
 * words directly beneath the quote is the third telling of one fact (the rationale section above
 * is the first), so the renderer suppresses this line when the quote is present. It is still
 * produced here: callers without the quote beside them need the meaning stated.
 */
const CLEARED_BY_QUOTE_MEANING =
    "the affected feature is not configured, and the advisory states this clears the device";

/**
 * What this row means for the verdict, in the operator's terms.
 *
 * The refusal wording is deliberate: "could not clear this device" says what we failed to
 * establish, where "not configured" would assert something the advisory never licensed.
 */
function describeMeaning(row) {
    if (!row) {
        return "";
    }
    if (isMissingSnapshot(row)) {
        return (
            "no configuration snapshot for this device — capture it to enable config checks. " +
            "This check has not run, which is not the same as finding nothing"
        );
    }
    if (row.outcome === "invalid") {
        return row.invalidReason ? "check did not run: " + row.invalidReason : "check did not run";
    }
    // BEFORE the outcome cases below, because it overrides what they would have concluded. The check
    // ran and produced a real result; what it cannot do is clear the device, because the advisory's
    // command reads a narrower part of the device than the configuration we searched. Reporting the
    // outcome without this would state a clearance the evidence does not support.
    if (row.clearanceBlockedReason) {
        return "this check cannot clear the device — " + row.clearanceBlockedReason;
    }
    if (row.meaning === "disabled" && row.outcome === "matched") {
        return "the configuration explicitly disables the affected feature";
    }
    if (row.meaning === "enabled" && row.outcome === "matched") {
        return "the affected feature is present in the running configuration";
    }
    if (row.outcome === "absent" && row.sourceQuote) {
        return CLEARED_BY_QUOTE_MEANING;
    }
    if (row.outcome === "absent") {
        return "not found, but the advisory does not say what absence means — this cannot clear the device";
    }
    return "";
}

/** One-line summary for the collapsed remainder. */
function summarizeInconclusive(rows) {
    const count = (rows || []).length;
    if (count === 0) {
        return "";
    }
    return (
        count + " further check" + (count === 1 ? "" : "s") + " ran and could not clear this device"
    );
}

module.exports = {
    CLEARED_BY_QUOTE_MEANING,
    OUTCOME_LABELS,
    describeCeiling,
    isMissingSnapshot,
    isDecisive,
    partitionConfigEvidence,
    describeOutcome,
    describeMeaning,
    summarizeInconclusive,
};
